import React, { useEffect, useState } from "react";

const STORES = [
  { id: 0, name: "Todos los locales" },
  { id: 6, name: "Niko 1" },
  { id: 3, name: "Niko 2" },
  { id: 1, name: "Niko 3" },
  { id: 2, name: "Niko 4" },
  { id: 4, name: "Niko 5" },
  { id: 5, name: "Niko 7" },
];

const DIAS = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];
const DIAS_FULL = ["Domingo", "Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado"];
const PERIODOS = [{ v: 7, l: "7 dias" }, { v: 14, l: "14 dias" }, { v: 30, l: "30 dias" }, { v: 90, l: "90 dias" }];

interface HeatCell { dia: number; hora: number; total: number; }
interface HourlyData { hora: number; total: number; promedio: number; }
interface Props { token: string; }

function getColor(val: number, max: number): string {
  if (max === 0 || val === 0) return "#0d1b2a";
  const ratio = val / max;
  if (ratio < 0.2) return "#0d3b6e";
  if (ratio < 0.4) return "#1a6699";
  if (ratio < 0.6) return "#f39c12";
  if (ratio < 0.8) return "#e67e22";
  return "#e74c3c";
}

export default function AnalyticsPanel({ token }: Props) {
  const [storeId, setStoreId] = useState(0);
  const [dias, setDias] = useState(30);
  const [heatmap, setHeatmap] = useState<HeatCell[]>([]);
  const [hourly, setHourly] = useState<HourlyData[]>([]);
  const [loading, setLoading] = useState(false);

  const headers = { "Authorization": "Bearer " + token };

  const load = async () => {
    setLoading(true);
    try {
      const [hRes, hlyRes] = await Promise.all([
        fetch(`/api/analytics/heatmap?store_id=${storeId}&dias=${dias}`, { headers }),
        fetch(`/api/analytics/hourly?store_id=${storeId}&dias=${dias}`, { headers }),
      ]);
      setHeatmap(await hRes.json());
      setHourly(await hlyRes.json());
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [storeId, dias]);

  const getCell = (dia: number, hora: number) => {
    return heatmap.find(h => h.dia === dia && h.hora === hora)?.total || 0;
  };

  const maxVal = Math.max(...heatmap.map(h => h.total), 1);
  const maxHourly = Math.max(...hourly.map(h => h.total), 1);

  const horasPico = [...hourly].sort((a,b) => b.total - a.total).slice(0,3).map(h => `${h.hora}:00`).join(", ");
  const diaPico = (() => {
    const byDia: {[k: number]: number} = {};
    heatmap.forEach(h => { byDia[h.dia] = (byDia[h.dia] || 0) + h.total; });
    const maxD = Math.max(...Object.values(byDia));
    const key = Object.keys(byDia).find(k => byDia[+k] === maxD);
    return key !== undefined ? DIAS_FULL[+key] : "-";
  })();

  const totalEntradas = hourly.reduce((a, b) => a + b.total, 0);
  const card: React.CSSProperties = { background: "#1e2a3a", borderRadius: "8px", padding: "20px", marginBottom: "24px" };

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ color: "white", marginBottom: "24px" }}>Analytics — Horarios Calientes</h2>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px" }}>
        {STORES.map(s => (
          <button key={s.id} onClick={() => setStoreId(s.id)}
            style={{ padding: "8px 14px", borderRadius: "4px", border: "none", cursor: "pointer", fontWeight: "bold",
              background: storeId === s.id ? "#3498db" : "#2c3e50", color: "white" }}>
            {s.name}
          </button>
        ))}
        <select value={dias} onChange={e => setDias(+e.target.value)}
          style={{ padding: "8px 14px", borderRadius: "4px", border: "none", background: "#2c3e50", color: "white", cursor: "pointer" }}>
          {PERIODOS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
        </select>
      </div>

      {loading && <p style={{ color: "#aaa" }}>Cargando...</p>}

      <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
        <div style={{ ...card, flex: 1, minWidth: "150px", marginBottom: 0, textAlign: "center" }}>
          <p style={{ color: "#aaa", fontSize: "12px", margin: 0 }}>TOTAL ENTRADAS</p>
          <p style={{ color: "white", fontSize: "28px", fontWeight: "bold", margin: "8px 0 0" }}>{totalEntradas.toLocaleString()}</p>
        </div>
        <div style={{ ...card, flex: 1, minWidth: "150px", marginBottom: 0, textAlign: "center" }}>
          <p style={{ color: "#aaa", fontSize: "12px", margin: 0 }}>HORAS PICO</p>
          <p style={{ color: "#f39c12", fontSize: "20px", fontWeight: "bold", margin: "8px 0 0" }}>{horasPico || "-"}</p>
        </div>
        <div style={{ ...card, flex: 1, minWidth: "150px", marginBottom: 0, textAlign: "center" }}>
          <p style={{ color: "#aaa", fontSize: "12px", margin: 0 }}>DIA MAS ACTIVO</p>
          <p style={{ color: "#2ecc71", fontSize: "20px", fontWeight: "bold", margin: "8px 0 0" }}>{diaPico}</p>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ color: "white", marginBottom: "16px" }}>Heatmap — Entradas por dia y hora</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ color: "#aaa", padding: "4px 8px", textAlign: "left", minWidth: "60px" }}></th>
                {Array.from({length: 24}, (_, i) => (
                  <th key={i} style={{ color: "#aaa", padding: "4px 2px", textAlign: "center", fontSize: "11px", minWidth: "28px" }}>{i}h</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DIAS.map((dia, dIdx) => (
                <tr key={dIdx}>
                  <td style={{ color: "white", padding: "4px 8px", fontSize: "12px", fontWeight: "bold" }}>{dia}</td>
                  {Array.from({length: 24}, (_, hIdx) => {
                    const val = getCell(dIdx, hIdx);
                    return (
                      <td key={hIdx} title={`${dia} ${hIdx}:00 - ${val} entradas`}
                        style={{ background: getColor(val, maxVal), width: "28px", height: "28px",
                          borderRadius: "3px", border: "1px solid #0a1628" }}>
                        {val > 0 && <span style={{ display: "block", textAlign: "center", fontSize: "9px",
                          color: "rgba(255,255,255,0.8)", lineHeight: "28px" }}>{val}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "12px", alignItems: "center" }}>
          <span style={{ color: "#aaa", fontSize: "12px" }}>Menos</span>
          {["#0d3b6e","#1a6699","#f39c12","#e67e22","#e74c3c"].map(c => (
            <div key={c} style={{ width: "20px", height: "20px", background: c, borderRadius: "3px" }} />
          ))}
          <span style={{ color: "#aaa", fontSize: "12px" }}>Mas</span>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ color: "white", marginBottom: "16px" }}>Entradas por hora del dia</h3>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: "150px" }}>
          {Array.from({length: 24}, (_, i) => {
            const h = hourly.find(x => x.hora === i);
            const val = h?.total || 0;
            const height = maxHourly > 0 ? Math.round((val / maxHourly) * 130) : 0;
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                <div title={`${i}:00 - ${val} entradas`}
                  style={{ width: "100%", height: `${height}px`, minHeight: val > 0 ? "4px" : "0",
                    background: height > 80 ? "#e74c3c" : height > 50 ? "#f39c12" : "#3498db",
                    borderRadius: "3px 3px 0 0" }} />
                <span style={{ color: "#aaa", fontSize: "9px", marginTop: "4px" }}>{i}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
